import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import Picture from "../components/Picture";
import CTABand from "../components/sections/CTABand";
import { INSIGHTS, INSIGHT_CATEGORIES } from "../data/insights";

function ArticleRow({ article, index }) {
    return (
        <motion.article
            suppressHydrationWarning
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{
                duration: 0.8,
                delay: (index % 3) * 0.05,
                ease: [0.22, 1, 0.36, 1],
            }}
            className="group border-t border-[rgba(201,168,106,0.15)] last:border-b"
            data-testid={`insight-row-${article.slug}`}
        >
            <Link
                to={`/insights/${article.slug}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 transition-colors duration-500 ease-out"
            >
                <div className="md:col-span-5 lg:col-span-4">
                    <div className="relative overflow-hidden aspect-[5/3] [&>picture]:absolute [&>picture]:inset-0 [&>picture]:block [&>picture]:h-full [&>picture]:w-full">
                        <Picture
                            slug={article.cover}
                            alt={article.title}
                            sizes="(max-width: 768px) 100vw, 33vw"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                        />
                    </div>
                </div>
                <div className="md:col-span-7 lg:col-span-7 lg:col-start-6 flex flex-col justify-center">
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                        <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                            {INSIGHT_CATEGORIES[article.category] ||
                                article.category}
                        </span>
                        <span className="h-px w-8 bg-[#C9A86A]/40" />
                        <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]">
                            {article.readTime}
                        </span>
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-[44px] leading-[1.1] text-[#F3EFE7] transition-colors duration-500 group-hover:text-[#D9BC80]">
                        {article.title}
                    </h2>
                    <p className="mt-5 text-[#9A958A] text-base md:text-[17px] leading-[1.85] max-w-2xl">
                        {article.excerpt}
                    </p>
                    <div className="mt-7 flex items-center justify-between">
                        <span className="text-[12px] tracking-[0.22em] uppercase text-[#9A958A]">
                            {article.date}
                        </span>
                        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#C9A86A]/50 text-[#C9A86A] transition-all duration-500 ease-out group-hover:bg-[#C9A86A] group-hover:text-[#0B0B0C]">
                            <ArrowUpRight size={16} strokeWidth={1.2} />
                        </span>
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}

export default function Insights() {
    return (
        <div data-testid="insights-page">
            <SEO
                title="Insights — Design & Technology Notes for Luxury Residences"
                description="Technology and design thinking for luxury residences — field notes, technical briefs, and partner interviews from Advanced Connected Technologies."
                canonical="https://actluxe.net/insights"
            />
            {/* Hero */}
            <section className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-12 md:pb-16 border-b border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <SectionLabel number="Insights" label="Journal" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
                            Notes on the{" "}
                            <span className="italic text-[#C9A86A]">
                                connected home.
                            </span>
                        </h1>
                        <p className="lg:col-span-4 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-2">
                            Field notes, technical briefs, and the occasional
                            opinion — written by the studio, for the principals,
                            architects, and designers who entrust us with the
                            residences we keep.
                        </p>
                    </div>
                </div>
            </section>

            {/* Article list */}
            <section className="bg-[#0B0B0C] overflow-hidden">
                <div className="shell pt-4 pb-16 md:pb-24">
                    {INSIGHTS.map((a, i) => (
                        <ArticleRow key={a.slug} article={a} index={i} />
                    ))}
                </div>
            </section>

            <CTABand />
        </div>
    );
}
